/**
 * helpers.js — Pure formatting and wage-calculation utilities
 *
 * All functions here are side-effect-free. No React, no API calls.
 * They mirror the logic from the legacy Directus custom JavaScript libraries:
 *   - `Utils`   → formatDuration, formatStatus, formatCurrency, formatDate, formatDateTime
 *   - `WagesJS` → parseTimeToDate, getShiftDuration, getHourlyRate
 *
 * Keeping these in sync with WagesJS is critical: if WagesJS changes on the
 * Directus side, wage calculations in AttendanceDetailModal will diverge.
 */

// ── Duration formatting ───────────────────────────────────────────────────────

/**
 * Convert a minute count to a human-readable duration string.
 * Returns '—' for null/undefined to allow safe rendering of missing data.
 *
 * @param {number|null|undefined} minutes
 * @returns {string}  e.g. "2h 30m", "0h 45m", "—"
 */
export function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

// ── Time string parser ────────────────────────────────────────────────────────

/**
 * Parse a "HH:MM:SS" time string (as stored in Directus shift fields) into a
 * Date object set to today's date. The date portion doesn't matter — only the
 * time components are used by callers to compute duration differences.
 *
 * @param {string|null} timeStr - e.g. "09:00:00" or "18:30:00"
 * @returns {Date}
 */
export function parseTimeToDate(timeStr) {
  if (!timeStr) return new Date();
  const [h, m, s] = timeStr.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, s || 0, 0);
  return d;
}

// ── Shift duration in wage hours ──────────────────────────────────────────────

/**
 * Calculate billable shift hours from a shift template.
 * Mirrors WagesJS.getShiftDuration exactly.
 *
 * Formula: (out_time − in_time) − break_hrs
 * The `diffMs < 0` guard handles overnight shifts (e.g. 22:00–06:00) by
 * adding a full 24 hours when out_time < in_time.
 *
 * @param {{ in_time: string, out_time: string, break_hrs: string|number }} shift
 * @returns {number} Billable hours (decimal, e.g. 8.5)
 */
export function getShiftDuration(shift) {
  if (!shift) return 0;
  const inDate = parseTimeToDate(shift.in_time);
  const outDate = parseTimeToDate(shift.out_time);
  let diffMs = outDate - inDate;
  if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000; // overnight shift correction
  const shiftHours = diffMs / (1000 * 60 * 60);
  const breakHours = parseFloat(shift.break_hrs || 0);
  return shiftHours - breakHours;
}

// ── Hourly rate ───────────────────────────────────────────────────────────────

/**
 * Derive the per-hour wage rate from a rate card and shift template.
 * Mirrors WagesJS.getHourlyRate exactly.
 *
 * Directus stores a flat `daily_rate` on the rate card; this converts it to
 * an hourly figure based on the shift's billable hours.
 *
 * @param {{ in_time: string, out_time: string, break_hrs: string|number }} shift
 * @param {{ daily_rate: number }} rate
 * @returns {number} Rupees per hour
 */
export function getHourlyRate(shift, rate) {
  const wageHours = getShiftDuration(shift);
  if (!rate || !wageHours) return 0;
  return rate.daily_rate / wageHours;
}

// ── Status display ────────────────────────────────────────────────────────────

/**
 * Map raw Directus attendance status values to user-facing labels.
 * 'checkout' is shown as 'Finished' because supervisors see it as the shift
 * being done; managers see it as awaiting approval.
 *
 * @param {string} status - Raw Directus status value
 * @returns {string} Human-readable label
 */
export function formatStatus(status) {
  const map = {
    present: 'Present',
    unmarked: 'Unmarked',
    absent: 'Absent',
    checkout: 'Finished',
    break: 'On Break',
    approved: 'Approved',
  };
  return map[status] || status;
}

/**
 * Hex colour codes for each attendance status.
 * These exact values come from the Appsmith custom widget — keep them in sync
 * with the CSS variables in index.css (--status-*).
 */
export const STATUS_COLORS = {
  present:  '#268f00',
  unmarked: '#9c9c9c',
  absent:   '#b30000',
  break:    '#f0c816',
  checkout: '#e35700',
  approved: '#007c9e',
};

// ── Currency formatting ───────────────────────────────────────────────────────

/**
 * Format a rupee amount using the Indian numbering system (lakhs, crores).
 * Uses maximumFractionDigits: 0 because all stored amounts are whole rupees.
 * Returns '—' for null/undefined to allow safe rendering of missing balances.
 *
 * @param {number|null|undefined} amount
 * @returns {string}  e.g. "₹1,00,000", "₹500", "—"
 */
export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '—';
  return Number(amount).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ── Date helpers ──────────────────────────────────────────────────────────────

/**
 * Format a date string for display.
 *
 * @param {string|null|undefined} dateStr - ISO date or datetime string
 * @param {Intl.DateTimeFormatOptions} [options] - Defaults to "01 Jan 2024" style
 * @returns {string}
 */
export function formatDate(dateStr, options) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', options || {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/**
 * Format a full ISO timestamp as a time-only string (24-hour, en-IN locale).
 *
 * @param {string|null|undefined} tsStr - ISO 8601 timestamp
 * @returns {string}  e.g. "14:30:45", "—"
 */
export function formatDateTime(tsStr) {
  if (!tsStr) return '—';
  return new Date(tsStr).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

/**
 * Extract the YYYY-MM-DD portion from a Date or date string.
 * Used when building Directus filter query parameters.
 *
 * @param {Date|string} date
 * @returns {string}  e.g. "2024-01-15"
 */
export function toISODate(date) {
  return new Date(date).toISOString().slice(0, 10);
}

// ── IST today ─────────────────────────────────────────────────────────────────

/**
 * Return today's date string in IST (UTC+5:30).
 *
 * Directus stores attendance dates as plain "YYYY-MM-DD" strings anchored to
 * IST. If we used the browser's local timezone (which may be different on
 * some machines), the date boundary at midnight would be off by up to 5.5 h.
 * This function adds the 5.5-hour offset to UTC before slicing the date.
 *
 * @returns {string}  e.g. "2024-01-15"
 */
export function getISTToday() {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  return new Date(now.getTime() + istOffset).toISOString().slice(0, 10);
}
